import { useMsal, useMsalAuthentication } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { usePulseMe } from '@/app/auth/use-pulse-me'
import {
  clearLocallySignedOut,
  isLocallySignedOut,
  subscribeLocalSignOut,
} from '@/app/auth/local-logout'
import {
  clearShieldSession,
  getShieldVisionAccessToken,
} from '@/app/auth/shield-token-source'
import { isAuthSessionExpiredError } from '@/app/auth/session-expired-error'
import { HAS_DEV_JWT, USING_SHIELD_AUTH } from '@/lib/env'
import { loginRequest } from '@/app/auth/msal-config'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Gate the whole app on a valid session — MSAL redirect flow. If the dev-JWT
 * override is set (only in dev builds), skip MSAL entirely so a local operator
 * can work against staging Vision before the Entra app registration exists.
 */
export function Protected({ children }: { children: ReactNode }) {
  if (HAS_DEV_JWT) return <>{children}</>
  if (USING_SHIELD_AUTH) return <ShieldGated>{children}</ShieldGated>
  return <MsalGated>{children}</MsalGated>
}

function ShieldGated({ children }: { children: ReactNode }) {
  if (useLocalSignedOut()) return <SignedOut />
  return <ShieldAuthenticated>{children}</ShieldAuthenticated>
}

function ShieldAuthenticated({ children }: { children: ReactNode }) {
  const me = usePulseMe()
  const authFailed = me.isError && isBootstrapAuthFailure(me.error)

  useEffect(() => {
    if (authFailed) clearShieldSession()
  }, [authFailed])

  if (!me.data && (me.isLoading || me.isFetching)) {
    return <BootSkeleton label="Checking your Pulse access…" />
  }

  if (authFailed) return <SignedOut />

  if (me.isError) {
    return <AccessCheckFailed onRetry={() => void me.refetch()} />
  }

  return <>{children}</>
}

function isBootstrapAuthFailure(error: unknown): boolean {
  if (isAuthSessionExpiredError(error)) return true
  const status = (error as { status?: number } | null)?.status
  return status === 401 || status === 403
}

function MsalGated({ children }: { children: ReactNode }) {
  // Local sign-out clears MSAL cache but leaves the Entra SSO cookie. Skip the
  // auto-login hook so SSO does not silently put the operator straight back in.
  if (useLocalSignedOut()) return <SignedOut />
  return <MsalAuthenticated>{children}</MsalAuthenticated>
}

function useLocalSignedOut(): boolean {
  return useSyncExternalStore(subscribeLocalSignOut, isLocallySignedOut, () => false)
}

function MsalAuthenticated({ children }: { children: ReactNode }) {
  const { inProgress, accounts } = useMsal()
  const { login, error } = useMsalAuthentication(InteractionType.Redirect, loginRequest)

  if (error) {
    return (
      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="text-[15px] font-semibold text-[color:var(--crit)]">Sign-in failed</h1>
        <p className="mt-1 text-[12.5px] text-[color:var(--muted-foreground)]">
          {error.errorMessage || error.message}
        </p>
        <button
          type="button"
          onClick={() => void login(InteractionType.Redirect, loginRequest)}
          className="mt-3 rounded-md bg-[color:var(--card)] px-3 py-1.5 text-[12.5px] font-medium shadow-[var(--shadow-raised)] hover:bg-[color:var(--muted)]"
        >
          Try again
        </button>
      </div>
    )
  }

  if (inProgress !== 'none' || accounts.length === 0) {
    return <BootSkeleton />
  }

  return <>{children}</>
}

function SignedOut() {
  const { instance } = useMsal()

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-[15px] font-semibold text-[color:var(--foreground)]">
        Signed out of Pulse
      </h1>
      <p className="text-[12.5px] text-[color:var(--muted-foreground)]">
        {USING_SHIELD_AUTH
          ? 'Sign in again to open Pulse with your Shield session.'
          : 'Your Microsoft account is still signed in on this browser. Sign in again to open Pulse.'}
      </p>
      <Button
        size="sm"
        onClick={() => {
          clearLocallySignedOut()
          if (USING_SHIELD_AUTH) {
            void getShieldVisionAccessToken()
            return
          }
          void instance.loginRedirect(loginRequest)
        }}
      >
        Sign in
      </Button>
    </div>
  )
}

function AccessCheckFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
      <h1 className="text-[15px] font-semibold text-[color:var(--foreground)]">
        Could not verify Pulse access
      </h1>
      <p className="text-[12.5px] text-[color:var(--muted-foreground)]">
        Pulse could not finish the access check. Try again before opening the dashboard.
      </p>
      <Button size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  )
}

function BootSkeleton({ label = 'Signing you in…' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="auth-bootstrap-loader"
      className="grid min-h-screen place-items-center bg-[color:var(--background)] px-6"
    >
      <div className="flex w-full max-w-2xl flex-col items-center gap-5">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="size-8 rounded-full bg-[color:var(--primary)] bf-breathe" aria-hidden />
          <span className="text-[11.5px] font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]">
            {label}
          </span>
        </div>
        <div
          aria-hidden
          className="w-full rounded-lg bg-[color:var(--card)] p-4 shadow-[var(--shadow-raised)]"
        >
          <div className="mb-4 flex items-center justify-between gap-4">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-8 w-20" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
          <div className="mt-4 space-y-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        </div>
      </div>
    </div>
  )
}
